if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.revealWhenReady();
        this.preloadViewerImages();
        this.revealThumbnailsWhenReady();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          button.addEventListener('mouseenter', () => this.preloadMediaById(mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      revealThumbnailsWhenReady() {
        const thumbnailSlider = this.elements.thumbnails;
        if (!thumbnailSlider?.classList.contains('thumbnail-slider--reveal')) return;
        if (thumbnailSlider.classList.contains('thumbnail-slider--ready')) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          thumbnailSlider.classList.add('thumbnail-slider--ready');
          return;
        }

        const images = Array.from(thumbnailSlider.querySelectorAll('.thumbnail img'));
        const reveal = () => thumbnailSlider.classList.add('thumbnail-slider--ready');

        if (!images.length) {
          reveal();
          return;
        }

        Promise.all(images.map((image) => this.ensureImageLoaded(image))).then(reveal);
      }

      revealWhenReady() {
        if (!this.classList.contains('media-gallery--reveal') || this.classList.contains('media-gallery--ready')) {
          return;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.classList.add('media-gallery--ready');
          return;
        }

        const activeSlide =
          this.querySelector('.product__media-item.is-active') || this.querySelector('.product__media-item');
        const activeImage = activeSlide?.querySelector('.product__modal-opener--image img, .product__media img');

        const reveal = () => {
          this.classList.add('media-gallery--ready');
        };

        if (!activeImage) {
          reveal();
          return;
        }

        if (activeImage.complete) {
          reveal();
        } else {
          activeImage.addEventListener('load', reveal, { once: true });
          activeImage.addEventListener('error', reveal, { once: true });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const shouldAnimate =
          !prepend && !!this.elements.thumbnails && !activeMedia.classList.contains('is-active');

        const applyActiveMedia = () => {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active', 'is-fading-in');
          });
          activeMedia?.classList?.add('is-active');

          if (shouldAnimate) {
            requestAnimationFrame(() => {
              activeMedia.classList.add('is-fading-in');
              activeMedia.addEventListener(
                'animationend',
                (event) => {
                  if (event.animationName === 'product-media-switch-in') {
                    activeMedia.classList.remove('is-fading-in');
                  }
                },
                { once: true }
              );
            });
          }

          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }

            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }

          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
          this.playActiveMedia(activeMedia);

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        };

        if (!prepend && this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
        }

        const activeImage = activeMedia.querySelector('.product__modal-opener--image img, .product__media img');
        if (!prepend && activeImage && !(activeImage.complete && activeImage.naturalWidth > 0)) {
          this.ensureImageLoaded(activeImage).then(applyActiveMedia);
          return;
        }

        applyActiveMedia();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      preloadViewerImages() {
        this.querySelectorAll('.product__media-item').forEach((slide) => {
          this.preloadMediaById(slide.dataset.mediaId);
        });
      }

      preloadMediaById(mediaId) {
        const slide = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
        const image = slide?.querySelector('.product__modal-opener--image img, .product__media img');
        if (image) this.ensureImageLoaded(image);
      }

      ensureImageLoaded(image) {
        if (image.complete && image.naturalWidth > 0) return Promise.resolve();

        return new Promise((resolve) => {
          const onDone = () => resolve();

          image.addEventListener('load', onDone, { once: true });
          image.addEventListener('error', onDone, { once: true });

          if (image.loading === 'lazy') image.loading = 'eager';

          const preload = new Image();
          preload.addEventListener('load', onDone, { once: true });
          preload.addEventListener('error', onDone, { once: true });
          if (image.sizes) preload.sizes = image.sizes;
          if (image.srcset) preload.srcset = image.srcset;
          preload.src = image.currentSrc || image.src;
        });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
